import React, { createContext, useContext, useState, useEffect } from 'react';
import { http } from '../lib/http';

const AuthContext = createContext();

// UI-ONLY MOCK MODE FLAG
const MOCK_MODE = false;

const MOCK_USER = {
  id: 'mock-owner',
  full_name: 'Mock Owner (UI Mode)',
  email: 'owner@justprotein.com',
  role: 'owner',
  dashboards: ['owner', 'admin', 'kitchen', 'driver', 'packaging', 'customer', 'trucker']
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(MOCK_MODE ? MOCK_USER : null);
  const [token, setToken] = useState(MOCK_MODE ? 'mock-token' : localStorage.getItem('token'));
  const [loading, setLoading] = useState(MOCK_MODE ? false : true);
  console.log("🛡️ AuthProvider state:", { user, token, loading, MOCK_MODE });
  const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

  // Configure http defaults with token
  useEffect(() => {
    if (MOCK_MODE) return;
    if (token) {
      http.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete http.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check authentication status on mount
  useEffect(() => {
    if (MOCK_MODE) {
      setLoading(false);
      return;
    }
    const checkAuth = async () => {
      if (token) {
        // Try customer auth first, then staff auth as fallback
        let authenticated = false;
        try {
          const response = await http.get('/auth/me');
          setUser(response.data);
          authenticated = true;
        } catch {
          // Customer auth failed — try staff auth
          try {
            const response = await http.get('/staff/me');
            setUser(response.data);
            authenticated = true;
          } catch {
            // Both failed — token is invalid
          }
        }
        if (!authenticated) {
          setToken(null);
          setUser(null);
          localStorage.removeItem('token');
          delete http.defaults.headers.common['Authorization'];
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    if (MOCK_MODE) {
      setUser(MOCK_USER);
      setToken('mock-token');
      return { success: true, user: MOCK_USER };
    }
    try {
      const response = await http.post('/auth/login', {
        email,
        password
      });

      const { access_token, refreshToken, user: userData } = response.data;

      // Check if 2FA is required
      if (access_token === '2FA_REQUIRED' && userData.requires_2fa) {
        return {
          success: true,
          requires2FA: true,
          email: userData.email,
          name: userData.full_name
        };
      }

      // Normal login flow
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      localStorage.setItem('userType', 'customer');
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      http.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return { success: true, user: userData };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const staffLogin = async (email, password) => {
    if (MOCK_MODE) {
      setUser(MOCK_USER);
      setToken('mock-token');
      return { success: true, user: MOCK_USER };
    }
    try {
      const response = await http.post('/staff/login', { email, password });
      const { access_token, refreshToken, user: userData } = response.data;

      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      localStorage.setItem('userType', 'staff');
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      http.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return { success: true, user: userData };
    } catch (error) {
      console.error('Staff login failed:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Invalid email or password',
      };
    }
  };

  const register = async (userData) => {
    if (MOCK_MODE) return { success: true, user: MOCK_USER };
    try {
      const response = await http.post('/auth/register', userData);
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Registration failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    if (!MOCK_MODE) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userType');
      delete http.defaults.headers.common['Authorization'];
    }

    // Force redirect to landing page
    window.location.href = '/';
  };

  const complete2FA = (access_token, userData) => {
    if (MOCK_MODE) return;
    // Update state
    setToken(access_token);
    setUser(userData);

    // Store in localStorage
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('userType', 'customer');

    // Set auth header
    http.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
  };

  const isAuthenticated = !!token && !!user;
  const isOwner = user?.role === 'owner';
  const isAdmin = user?.role === 'admin';
  const isOwnerOrAdmin = isOwner || isAdmin;
  const isStaff = user?.role && ['owner', 'admin', 'kitchen', 'driver', 'packaging'].includes(user.role);

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    isOwner,
    isAdmin,
    isOwnerOrAdmin,
    isStaff,
    login,
    staffLogin,
    register,
    logout,
    complete2FA,
    API_BASE
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};